/**
 * Heuristic activity classifier for the live accounting view.
 *
 * Implements the classification model from the Engineering-Costs
 * planning spec (`Engineering_Costs_Planning_v2.md`): every commit
 * gets two independent classifications —
 *
 *   - Investment type: KTLO | Enablement | Growth
 *   - Accounting treatment: OpEx{R&D,COGS,S&M,G&A} | CapEx
 *
 * with per-axis candidate confidences, a rule trace (the audit
 * trail the spec requires on every activity), and a `flagged_for_
 * review` bit when the top-two candidates on *either* axis land
 * within the 0.15 confidence threshold.
 *
 * ── Scope / known limitations (POC) ──────────────────────────────
 *  • Signal source is the commit *subject* plus its derived business
 *    function. The spec's worked examples are overwhelmingly title-
 *    driven ("Fix off-by-one…", "Add SAML SSO…", "Bump lodash…"),
 *    so subject-keyword rules track the spec well. File paths and PR
 *    labels — richer signals the spec also names — aren't available
 *    client-side from the activity feed; the eventual server composer
 *    (reading `file_clusters` + raw paths + labels) can layer them in.
 *  • Treatment is classified by the *nature of the activity* per ASC
 *    350-40's stage test — no management-approval gate. CapEx
 *    candidates are net-new functionality that is past the preliminary
 *    (exploration) stage and not yet post-implementation (maintenance);
 *    everything else expenses. The classifier surfaces what looks
 *    capitalizable; formal capitalization sign-off happens downstream
 *    in the accounting close, so CapEx commits carry no linked project
 *    here. Gray-area net-new work reached via refactors / dependency
 *    bumps defaults conservatively to OpEx and is flagged for review.
 */

import type {
  AccountingTreatment,
  ClassificationDecision,
  ClassificationRuleTrace,
  ClassificationSuggestion,
  ClassifierCandidate,
  InvestmentType,
  OpExSubtype,
} from './types';

export const CLASSIFIER_VERSION = 'heuristic-subject-v1';

/** Top-two candidates within this delta on either axis → flag for review. */
export const REVIEW_DELTA_THRESHOLD = 0.15;

export interface ClassifyInput {
  subject: string;
  /** Display name of the derived business function (nudges OpEx subtype). */
  businessFunctionDisplay: string | null;
  /** Unix epoch seconds — stamped onto the decision. */
  committedAt: number;
}

export interface ClassifyResult {
  suggestion: ClassificationSuggestion;
  decision: ClassificationDecision;
  flagged_for_review: boolean;
}

// ---- Investment-type keyword rules --------------------------------------
//
// Each rule, when it matches the subject, adds `weight` to one
// investment type and records a trace entry. Conventional-commit
// prefixes (feat:/fix:/chore:…) are the strongest single signal, so
// they carry the most weight. A small uniform prior keeps every
// candidate non-zero — and, crucially, makes a subject that matches
// *nothing* come out as a three-way tie, which trips the review flag
// (exactly the "classifier isn't sure → human looks" behaviour the
// spec wants).

interface InvestmentRule {
  id: string;
  description: string;
  type: InvestmentType;
  weight: number;
  test: RegExp;
}

const PRIOR = 0.4;

const INVESTMENT_RULES: InvestmentRule[] = [
  // ── Conventional-commit prefixes (strongest signal) ──
  {
    id: 'cc-prefix-feat',
    description: 'Conventional-commit `feat:` prefix — net-new capability',
    type: 'growth',
    weight: 3,
    test: /^\s*feat(\(|:|!)/i,
  },
  {
    id: 'cc-prefix-fix',
    description: 'Conventional-commit `fix:` prefix — maintenance of shipped code',
    type: 'ktlo',
    weight: 3,
    test: /^\s*(fix|hotfix|bugfix|revert|perf)(\(|:|!)/i,
  },
  {
    id: 'cc-prefix-internal',
    description: 'Conventional-commit `chore:`/`ci:`/`build:`/`test:`/`refactor:`/`docs:` prefix — internal/eng work',
    type: 'enablement',
    weight: 3,
    test: /^\s*(chore|ci|build|test|refactor|docs|style|lint)(\(|:|!)/i,
  },

  // ── KTLO keywords — work that exists because the product is in prod ──
  {
    id: 'ktlo-maintenance',
    description: 'Bug fix / regression / crash on existing production code',
    type: 'ktlo',
    weight: 2,
    test: /\b(fix(es|ed)?|bug|hotfix|patch(es|ed)?|regression|crash|broken|error|exception|rollback|revert)\b/i,
  },
  {
    id: 'ktlo-dependency',
    description: 'Dependency bump / version upgrade / CVE remediation',
    type: 'ktlo',
    weight: 2,
    test: /\b(bump|upgrade|update)\b.*\b(dep(s|endency|endencies)?|version|lockfile|package)\b|\bcve-|\bvuln(erability)?\b|\bdependabot\b|\brenovate\b/i,
  },
  {
    id: 'ktlo-ops',
    description: 'Production reliability — monitoring, alerts, indexes, secrets, certs, EOL migrations',
    type: 'ktlo',
    weight: 2,
    test: /\b(alert|monitor(ing)?|on-?call|incident|index|slow query|timeout|retry|rate.?limit|rotate|secret|cert(ificate)?|deprecat(e|ed|ion)|eol|memory leak|flaky)\b/i,
  },

  // ── Enablement keywords — makes the rest of eng/the company effective ──
  {
    id: 'enablement-pipeline',
    description: 'CI/CD, build, lint, format, or test infrastructure',
    type: 'enablement',
    weight: 2,
    test: /\b(ci|cd|pipeline|workflow|build system|lint(er|ing)?|eslint|prettier|format(ting)?|coverage|test(s|ing| suite)?)\b/i,
  },
  {
    id: 'enablement-devx',
    description: 'Developer experience — tooling, local dev, docker, onboarding, scripts',
    type: 'enablement',
    weight: 2,
    test: /\b(tooling|devtool|dev ?ex|dx|docker|compose|local dev|dev env(ironment)?|onboarding|scaffold|codegen tool|makefile|script(s)?)\b/i,
  },
  {
    id: 'enablement-internal',
    description: 'Internal docs, admin tools, internal dashboards, refactors, chores',
    type: 'enablement',
    weight: 1,
    test: /\b(refactor|cleanup|clean up|chore|readme|docs?|documentation|wiki|internal|admin tool|internal dashboard)\b/i,
  },

  // ── Growth keywords — net-new customer-facing capability ──
  {
    id: 'growth-new-capability',
    description: 'Adds / introduces / implements net-new functionality',
    type: 'growth',
    weight: 2,
    test: /\b(add(s|ed|ing)?|new|introduc(e|ed|ing)|implement(s|ed|ing)?|launch(es|ed)?|ship(s|ped)?|build(s|ing)? a)\b/i,
  },
  {
    id: 'growth-surface',
    description: 'New customer-facing surface — endpoint, feature, integration, page, model',
    type: 'growth',
    weight: 1,
    test: /\b(feature|endpoint|integration|integrate|support for|new (api|page|screen|model|product|tier)|sso|saml|oauth|webhook)\b/i,
  },
];

// ---- OpEx subtype nudges (from the business-function name) ---------------
//
// Spec §"How to assign the OpEx sub-category": inherit from repo /
// directory / label tags. We don't have those client-side, so we nudge
// from the business-function display name and otherwise default to R&D
// (the spec's default sub-category for Growth and non-capitalizing KTLO).

interface SubtypeRule {
  subtype: OpExSubtype;
  test: RegExp;
}

const SUBTYPE_RULES: SubtypeRule[] = [
  { subtype: 'cogs', test: /\b(infra(structure)?|hosting|cloud ops|sre|reliability|serving|inference)\b/i },
  { subtype: 'ga', test: /\b(internal (it|tool)|corp(orate)?|workday|netsuite|salesforce|compliance|finance)\b/i },
  { subtype: 'sm', test: /\b(marketing|sales|demo|landing|website|growth site)\b/i },
];

function opexSubtypeFor(businessFunctionDisplay: string | null): OpExSubtype {
  if (businessFunctionDisplay) {
    for (const r of SUBTYPE_RULES) {
      if (r.test.test(businessFunctionDisplay)) return r.subtype;
    }
  }
  return 'rd';
}

// ---- Accounting treatment (ASC 350-40 activity-stage test) ---------------
//
// CapEx candidacy is a property of the *work*, not of a project sign-off.
// Only Growth (net-new functionality) is even a candidate; KTLO/Enablement
// expense. Among Growth, exploration/feasibility work is preliminary-stage
// (expense), and net-new work reached indirectly (a refactor or dependency
// bump that a feature happens to need) defaults conservatively to OpEx and
// flags — per the spec's gray-area guidance.

/** Exploration / feasibility signals — preliminary stage, always expensed. */
const PRELIMINARY_RE =
  /\b(explor\w+|prototyp\w+|poc|proof[- ]of[- ]concept|spike|experiment\w*|feasibilit\w+|investigat\w+|draft|wip|work[- ]in[- ]progress|rfc|throwaway|sketch|scratch)\b/i;

/** Net-new capability reached *indirectly* — capitalizable but gray-area. */
const CONSERVATIVE_GROWTH_RE =
  /\b(refactor\w*|clean ?up|bump|upgrad\w+|dependenc\w+|deps|migrat\w+)\b/i;

interface TreatmentResult {
  candidates: Array<ClassifierCandidate<AccountingTreatment>>;
  opexSubtype: OpExSubtype;
  trace: ClassificationRuleTrace;
}

function classifyTreatment(
  investmentWinner: InvestmentType,
  subject: string,
  businessFunctionDisplay: string | null,
): TreatmentResult {
  const opexSubtype = opexSubtypeFor(businessFunctionDisplay);

  // Non-growth → expense. Maintenance is post-implementation; internal-use
  // enablement rarely capitalizes.
  if (investmentWinner !== 'growth') {
    return {
      candidates: [
        { type: 'opex', confidence: 0.9 },
        { type: 'capex', confidence: 0.1 },
      ],
      opexSubtype,
      trace: {
        rule_id: 'treatment-opex-nongrowth',
        rule_description:
          'Maintenance / enablement work is expensed — post-implementation or internal-use software (ASC 350-40)',
        matched_input: `investment type is ${investmentWinner}`,
      },
    };
  }

  // Growth, but exploratory → preliminary stage, expense as R&D.
  const prelim = PRELIMINARY_RE.exec(subject);
  if (prelim) {
    return {
      candidates: [
        { type: 'opex', confidence: 0.82 },
        { type: 'capex', confidence: 0.18 },
      ],
      opexSubtype,
      trace: {
        rule_id: 'treatment-opex-preliminary',
        rule_description:
          'Net-new work in the preliminary / exploration stage is expensed (ASC 350-40 preliminary stage)',
        matched_input: `subject signals exploration: “${prelim[0].trim()}”`,
      },
    };
  }

  // Growth reached indirectly (refactor / dependency) → defensible CapEx,
  // but conservative default is OpEx; land them close so they flag.
  const indirect = CONSERVATIVE_GROWTH_RE.exec(subject);
  if (indirect) {
    return {
      candidates: [
        { type: 'opex', confidence: 0.55 },
        { type: 'capex', confidence: 0.45 },
      ],
      opexSubtype,
      trace: {
        rule_id: 'treatment-graylisted-capex',
        rule_description:
          'Net-new capability reached via a refactor / dependency change — capitalization is defensible but the conservative default is OpEx; flagged for review',
        matched_input: `subject matched “${indirect[0].trim()}”`,
      },
    };
  }

  // Net-new functionality, past feasibility, not yet in production →
  // application-development stage → CapEx candidate.
  return {
    candidates: [
      { type: 'capex', confidence: 0.8 },
      { type: 'opex', confidence: 0.2 },
    ],
    opexSubtype,
    trace: {
      rule_id: 'treatment-capex-appdev',
      rule_description:
        'Net-new functionality past the preliminary stage and not yet post-implementation → application-development stage, CapEx candidate (ASC 350-40-25)',
      matched_input: 'net-new (Growth) functionality, build stage',
    },
  };
}

// ---- Scoring -------------------------------------------------------------

function softmaxCandidates(
  scores: Record<InvestmentType, number>,
): Array<ClassifierCandidate<InvestmentType>> {
  const total =
    scores.ktlo + scores.enablement + scores.growth || 1;
  return (Object.keys(scores) as InvestmentType[])
    .map((type) => ({ type, confidence: scores[type] / total }))
    .sort((a, b) => b.confidence - a.confidence);
}

function topTwoDelta<T extends string>(
  candidates: Array<ClassifierCandidate<T>>,
): number {
  if (candidates.length < 2) return 1;
  return candidates[0]!.confidence - candidates[1]!.confidence;
}

/**
 * Classify one commit. Pure + deterministic — same subject in, same
 * classification out, so the audit trail is reproducible.
 */
export function classifyCommit(input: ClassifyInput): ClassifyResult {
  const subject = input.subject ?? '';

  const scores: Record<InvestmentType, number> = {
    ktlo: PRIOR,
    enablement: PRIOR,
    growth: PRIOR,
  };
  const trace: ClassificationRuleTrace[] = [];

  for (const rule of INVESTMENT_RULES) {
    const m = rule.test.exec(subject);
    if (!m) continue;
    scores[rule.type] += rule.weight;
    trace.push({
      rule_id: rule.id,
      rule_description: rule.description,
      matched_input: `subject matched “${m[0].trim()}”`,
    });
  }

  // No investment rule firing means a three-way prior tie — the
  // classifier isn't sure, so it's a defensive fallback (and flags).
  const investmentRulesFired = trace.length;
  const investmentCandidates = softmaxCandidates(scores);
  const investmentWinner = investmentCandidates[0]!.type;

  // Treatment follows the ASC 350-40 activity-stage test (see
  // classifyTreatment) — CapEx is surfaced by the nature of the work, not
  // a project sign-off.
  const treatment = classifyTreatment(
    investmentWinner,
    subject,
    input.businessFunctionDisplay,
  );
  trace.push(treatment.trace);

  const noRuleFired = investmentRulesFired === 0;
  const suggestion: ClassificationSuggestion = {
    investment_type_candidates: investmentCandidates,
    treatment_candidates: treatment.candidates,
    rule_trace: noRuleFired ? [] : trace,
    classifier_version: CLASSIFIER_VERSION,
  };

  const investmentDelta = topTwoDelta(investmentCandidates);
  const treatmentDelta = topTwoDelta(treatment.candidates);
  const flagged =
    investmentDelta < REVIEW_DELTA_THRESHOLD ||
    treatmentDelta < REVIEW_DELTA_THRESHOLD;

  const accountingTreatment = treatment.candidates[0]!.type;
  const decision: ClassificationDecision = {
    investment_type: investmentWinner,
    accounting_treatment: accountingTreatment,
    // opex_subtype is set only when the treatment is OpEx.
    opex_subtype: accountingTreatment === 'opex' ? treatment.opexSubtype : null,
    // Formal capitalization (and any project linkage) happens downstream
    // in the accounting close — the classifier only surfaces candidates.
    accounting_project_id: null,
    source: noRuleFired ? 'system_fallback' : 'system_auto',
    decided_by_user_id: null,
    rationale: null,
    decided_at: input.committedAt,
  };

  return { suggestion, decision, flagged_for_review: flagged };
}
